import React from 'react'
import PageLayout from 'layouts/PageLayout'
import Footer from 'components/Footer'
import Panel from 'components/Panel'
import Button, { LinkButton } from 'components/Button'
import Caption from 'components/Caption'
import { Flex } from 'components/Box'
import { SsoTag } from 'modules/workspace/containers/WorkspacesDropdown'
import {
  goToLinkAccountUrl,
  useLinkAccountConfirmation,
} from 'modules/sso/operations'
import { useQueryParams } from 'utils'
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import GenericErrorView from 'views/GenericErrorView'
import { RouteParams, routes } from 'constants/routes'
import {
  StyledPanel,
  Row,
  Header,
  OperationAndLogoContainer,
  WorkspaceLogo,
  LinkedWorkspacesAvatarsContainer,
  LinkedWorkspaceLogo,
  ExtraWorkspaceAvatarPlaceholder,
  LinkedWorkspaceNames,
  ExtraWorkspaceLogo,
} from './LinkAccountConfirmationPage.styles'
import { useParams } from 'react-router-dom'
import { useToast } from 'containers/ToastManager'
import { WorkspaceMinimalIdentityFragment } from 'gqlTypes'

const LinkedWorkspacesAvatars = ({
  workspaces,
}: {
  workspaces: WorkspaceMinimalIdentityFragment[]
}) => {
  const linkedWorkspacesAvatars = workspaces
    .slice(0, 2)
    .map(workspace => (
      <LinkedWorkspaceLogo
        key={workspace.identifier}
        size="24px"
        src={workspace.avatar?.small}
      />
    ))

  const numberOfWorkspacesOutOfList =
    workspaces.length - linkedWorkspacesAvatars.length

  if (numberOfWorkspacesOutOfList > 0) {
    linkedWorkspacesAvatars.push(
      <ExtraWorkspaceLogo key="More workspaces" $size="24px">
        <ExtraWorkspaceAvatarPlaceholder
          hasMoreThanNineWorkspaces={
            numberOfWorkspacesOutOfList > 9
          }>{`+${numberOfWorkspacesOutOfList}`}</ExtraWorkspaceAvatarPlaceholder>
      </ExtraWorkspaceLogo>
    )
  }

  return <>{linkedWorkspacesAvatars}</>
}

const LinkedWorkspaces = ({
  workspaces,
}: {
  workspaces: WorkspaceMinimalIdentityFragment[]
}) => {
  if (workspaces.length === 0) return null

  const workspaceNames = workspaces.map(workspace => workspace.name)

  return (
    <Row data-testid="linked-workspaces">
      <OperationAndLogoContainer>
        <Header>With</Header>
        <LinkedWorkspacesAvatarsContainer>
          <LinkedWorkspacesAvatars workspaces={workspaces} />
        </LinkedWorkspacesAvatarsContainer>
      </OperationAndLogoContainer>
      <LinkedWorkspaceNames>{workspaceNames.join(', ')}</LinkedWorkspaceNames>
    </Row>
  )
}

export const LinkAccountConfirmationPage = () => {
  const { showToast } = useToast()
  const { workspaceId } = useParams<RouteParams<'SSO_LINK_ACCOUNT'>>()
  const queryParams = useQueryParams<'SSO_LINK_ACCOUNT'>()
  const result = useLinkAccountConfirmation(workspaceId)

  if (result.type === 'loading') {
    return <DynamicLoadingPage />
  }

  if (result.type === 'error') {
    if (result.message) showToast(result.message)

    return <GenericErrorView />
  }

  return (
    <PageLayout showHeader={false} footer={<Footer />}>
      <StyledPanel>
        <Panel.Title>Link Account</Panel.Title>
        <Panel.Body>
          <Caption>
            This will help you easily switch between this SSO workspace and the
            other workspaces you already belong to.
          </Caption>

          <Flex flexDirection="column">
            <Row data-testid="signin-workspace">
              <OperationAndLogoContainer>
                <Header>Linking</Header>
                <WorkspaceLogo
                  src={result.signInWorkspace.avatar?.small}
                  workspaceName={result.signInWorkspace.name}
                  size="24px"
                  UNSAFE_ADD_MARGIN
                />
              </OperationAndLogoContainer>

              {result.signInWorkspace.name}
              <SsoTag />
            </Row>
            <LinkedWorkspaces workspaces={result.linkedWorkspaces} />
          </Flex>
        </Panel.Body>
        <Panel.Footer>
          <LinkButton
            to={routes.SSO_TOS.create({
              workspaceId,
              query: queryParams,
            })}
            variant="secondary">
            Don&apos;t Link
          </LinkButton>
          <Button
            variant="primary"
            onClick={() =>
              goToLinkAccountUrl({
                url: result.signInWorkspace.ssoStartUrl,
                linkedAccountToken: result.ssoLinkToken,
                ...queryParams,
              })
            }>
            Confirm Link
          </Button>
        </Panel.Footer>
      </StyledPanel>
    </PageLayout>
  )
}
